import { createReducer } from '@reduxjs/toolkit';
import { kpiActions } from '../components-bl/Analytics/actions/kpiActions';
import {
  GroupedKpiDataItem,
  KpiMetricResponse,
  RevenueDistributionDataItem,
} from '../services/src/service/types/analytics/analytics';
import { metadataActions } from '../components-bl/Analytics/actions/metadataActions';

interface KpiStateEntry {
  data: KpiMetricResponse | null;
  loading: boolean;
  error: string | null;
  requestId?: string;
}

interface GroupedKpiStateEntry {
  data: GroupedKpiDataItem[];
  loading: boolean;
  error: string | null;
  requestId?: string;
}

interface RevenueDistributionStateEntry {
  data: RevenueDistributionDataItem | null;
  loading: boolean;
  error: string | null;
  requestId?: string;
}

export interface ProductsMetadataState {
  data: string[] | null;
  loading: boolean;
  error: string | null;
}

export interface DataInsightsState {
  kpiValuesList: {
    [kpiType: string]: KpiStateEntry;
  };
  groupedKpiValuesList: {
    [kpiType: string]: GroupedKpiStateEntry;
  };
  revenueDistributionValuesList: RevenueDistributionStateEntry;
  productsMetadata: ProductsMetadataState;
}

const initialState: DataInsightsState = {
  kpiValuesList: {},
  groupedKpiValuesList: {},
  revenueDistributionValuesList: {
    data: null,
    loading: false,
    error: null,
  },
  productsMetadata: {
    data: null,
    loading: false,
    error: null,
  },
};

export const dataInsightsReducer = createReducer(initialState, builder => {
  builder
    .addCase(kpiActions.getKpi.pending, (state, action) => {
      const { kpiType } = action.meta.arg;
      if (!kpiType) return;

      state.kpiValuesList[kpiType] = {
        data: null,
        loading: true,
        error: null,
        requestId: action.meta.requestId,
      };
    })

    .addCase(kpiActions.getKpi.fulfilled, (state, action) => {
      const { kpiType, data } = action.payload;
      if (!kpiType) return;
      if (state.kpiValuesList[kpiType]?.requestId !== action.meta.requestId) return;

      state.kpiValuesList[kpiType] = {
        data,
        loading: false,
        error: null,
      };
    })

    .addCase(kpiActions.getKpi.rejected, (state, action) => {
      const kpiType = action.meta?.arg?.kpiType;
      if (!kpiType) return;
      if (state.kpiValuesList[kpiType]?.requestId !== action.meta.requestId) return;

      const errorMessage =
        (action.payload as any)?.error?.message || action.error?.message || 'Unknown error';

      state.kpiValuesList[kpiType] = {
        data: null,
        loading: false,
        error: errorMessage,
      };
    })

    .addCase(kpiActions.getGroupedKpi.pending, (state, action) => {
      const { kpiType } = action.meta.arg;
      if (!kpiType) return;
      state.groupedKpiValuesList[kpiType] = {
        data: [],
        loading: true,
        error: null,
        requestId: action.meta.requestId,
      };
    })

    .addCase(kpiActions.getGroupedKpi.fulfilled, (state, action) => {
      const { kpiType, data } = action.payload;
      if (!kpiType) return;
      if (state.groupedKpiValuesList[kpiType]?.requestId !== action.meta.requestId) return;
      state.groupedKpiValuesList[kpiType] = {
        data,
        loading: false,
        error: null,
      };
    })

    .addCase(kpiActions.getGroupedKpi.rejected, (state, action) => {
      const kpiType = action.meta?.arg?.kpiType;
      if (!kpiType) return;
      if (state.groupedKpiValuesList[kpiType]?.requestId !== action.meta.requestId) return;
      const errorMessage =
        (action.payload as any)?.error?.message || action.error?.message || 'Unknown error';
      state.groupedKpiValuesList[kpiType] = {
        data: [],
        loading: false,
        error: errorMessage,
      };
    })

    .addCase(kpiActions.getRevenueDistribution.pending, (state, action) => {
      state.revenueDistributionValuesList = {
        data: null,
        loading: true,
        error: null,
        requestId: action.meta.requestId,
      };
    })

    .addCase(kpiActions.getRevenueDistribution.fulfilled, (state, action) => {
      if (state.revenueDistributionValuesList.requestId !== action.meta.requestId) return;
      const { data } = action.payload;
      state.revenueDistributionValuesList = {
        data,
        loading: false,
        error: null,
      };
    })

    .addCase(kpiActions.getRevenueDistribution.rejected, (state, action) => {
      if (state.revenueDistributionValuesList.requestId !== action.meta.requestId) return;
      const errorMessage =
        (action.payload as any)?.error?.message || action.error?.message || 'Unknown error';
      state.revenueDistributionValuesList = {
        data: null,
        loading: false,
        error: errorMessage,
      };
    })

    .addCase(metadataActions.getProductsMetadata.pending, state => {
      state.productsMetadata = {
        data: null,
        loading: true,
        error: null,
      };
    })
    .addCase(metadataActions.getProductsMetadata.fulfilled, (state, action) => {
      state.productsMetadata = {
        data: action.payload,
        loading: false,
        error: null,
      };
    })
    .addCase(metadataActions.getProductsMetadata.rejected, (state, action) => {
      const errorMessage =
        (action.payload as any)?.error?.message || action.error?.message || 'Unknown error';
      state.productsMetadata = {
        data: null,
        loading: false,
        error: errorMessage,
      };
    });
});

export default dataInsightsReducer;
